import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { actionCreators } from 'react/api/actions/chat';
import { getResourceTo } from 'react/utils/utils';
import { DefaultRequest } from 'scripts/api';

import SearchContacts from 'static/images/chat/search.svg';
import SendIcon from 'static/images/chat/sendMessage.svg';
import Loading from 'react/components/Loading';

function UsersTab({
  addNewChatWindow,
  sendMessage
}) {
  const inputRef = useRef(null);
  const textAreaRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState(() => {
    const usersInSession = Monet.Session?.Chat?.Users;

    return usersInSession ? parseUsersData(usersInSession) : [];
  });
  const [selectedUsers, setSelectedUsers] = useState([]);

  function removeLoggedUser(listOfUsers) {
    return listOfUsers.filter((item) => item.id !== Monet.Session.User.id)
  }

  function parseUsersData(listOfUsers) {
    return removeLoggedUser(listOfUsers)
      .sort(Monet.Utils.sortComparator);
  }

  function handleSelectAllUsers() {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users);
    }
  }

  function handleSelectUser(user) {
    const updatedSelectedUsers = [...selectedUsers];

    if (updatedSelectedUsers.includes(user)) {
      const userIndex = updatedSelectedUsers.indexOf(user);

      updatedSelectedUsers.splice(userIndex, 1);
    } else {
      updatedSelectedUsers.push(user);
    }

    setSelectedUsers(updatedSelectedUsers);
  }

  function handleClickUser(user) {
    const payload = {
      from: user.id,
      fromName: user.name
    };

    addNewChatWindow(payload);
  }

  function handleSendMessage() {
    const text = textAreaRef.current.value.trim();

    if (text.length) {
      const toList = selectedUsers.map((user) => user.id);

      textAreaRef.current.value = '';

      sendMessage({
        message: text,
        to: toList
      });
    }
  }

  function handleSearchUsers() {
    const usersInSession = Monet.Session?.Chat?.Users;
    const parsedUsersData = parseUsersData(usersInSession);
    const value = inputRef.current.value;

    if (!value.length) {
      setUsers(parsedUsersData);
    }

    const findUsers = parsedUsersData
      .filter((user) => user.name.includes(value) || user.key.includes(value));

    setUsers(findUsers);
  }

  useEffect(() => {
    if (!Monet.Session?.Chat?.Users) {
      setIsLoading(true);

      new DefaultRequest({
        method: 'GET',
        url: 'Chat/Users?queryParameter=',
        success: (response) => {
          const parsedData = parseUsersData(response);

          setUsers(parsedData);
          setIsLoading(false);
          Monet.Session.Chat.Users = response;
        }
      }).send();
    }
  }, []);

  return (
    <>
      {isLoading && <Loading height="150px" />}
      {!isLoading && (
        <>
          <div className="users-list-header">
            <div className="key-column">
              {getResourceTo('suspenseActions.usersTab.code')}
            </div>
            <div className="name-column">
              {getResourceTo('suspenseActions.usersTab.name')}
            </div>
          </div>
          <div
            className="users-list-body"
            data-testid="users-list-body"
          >
            {users.length > 0 && (
              <div
                data-testid="select-all-button"
                className="select-all"
                onClick={handleSelectAllUsers}
              >
                <input
                  type="checkbox"
                  checked={selectedUsers.length === users.length}
                  readOnly
                />
                <span>{getResourceTo('suspenseActions.usersTab.selectAll')}</span>
              </div>
            )}
            {users.map((user) => (
              <div
                key={user.id}
                className={`user-list-item ${selectedUsers.includes(user) ? 'checked' : ''}`}
                data-testid={`user-list-item-${user.key}`}
              >
                <input
                  data-testid={`user-item-input-${user.key}`}
                  type="checkbox"
                  onChange={() => handleSelectUser(user)}
                  checked={selectedUsers.includes(user)}
                />
                <div
                  data-testid={`user-item-content-${user.key}`}
                  onClick={() => handleClickUser(user)}
                >
                  <p className="key-text">{user.key}</p>
                  <p className="name-text">{user.name}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="users-list-footer">
        {selectedUsers.length > 0 && (
          <div
            data-testid="broadcast-container"
            className="broadcast-container"
          >
            <div className="inputContainer">
              <textarea
                data-testid="broadcast-textarea"
                ref={textAreaRef}
                placeholder={getResourceTo('Greenmile.Chat.TextArea.Placeholder')}
              />
            </div>
            <div className="sendMessageAction">
              <button
                data-testid="broadcast-button"
                type="button"
                onClick={() => handleSendMessage()}
              >
                <img src={SendIcon} alt="Send Message Icon" />
              </button>
            </div>
          </div>
        )}
        <div className="search-container">
          <input
            data-testid="search-input"
            ref={inputRef}
            type="text"
            placeholder={getResourceTo('suspenseActions.usersTab.searchContactsPlaceholder')}
          />
          <button
            data-testid="search-button"
            type="button"
            onClick={handleSearchUsers}
          >
            <img src={SearchContacts} alt="Search Icon" />
          </button>
        </div>
      </div>
    </>
  );
}

UsersTab.propTypes = {
  addNewChatWindow: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired
};

const mapDispatchToProps = {
  addNewChatWindow: actionCreators.addNewChatWindow,
  sendMessage: actionCreators.sendMessage
};

export default connect(null, mapDispatchToProps)(UsersTab);
